import React, { FC, useCallback, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import {
	disabledByAnotherAction,
	getWorkOrderTimeZoneId,
	isSupplierPayingCustomerAndAdmin,
	nullSafeGet,
	nullSafeGetOrElse,
} from '../../utils/DataAccessUtils';
import DateTimeHover from '../date_time_component/DateTime';
import { UserName } from '../name_component/user/UserNameComponent';
import { ProtectedImageThumbnails } from '../protected_image_thumbnails/ProtectedImageThumbnails';
import { Button, Card, Divider, Popover, Timeline } from 'antd';
import { isInternalTech, PERMISSION_NAMES } from '../../utils/AuthUtils';
import AccessPermissionChecker from '../common/AccessPermissionChecker';
import { ContactAvatar } from '../contact_avatar/ContactAvatar';
import {
	getSupplierWorkOrderServiceCalls,
	isDifferentAssignedSupplierBasedOnView,
	isSubContractedWorkOrder,
} from '../../utils/WorkOrderUtils';
import { CopyOutlined, EditOutlined } from '@ant-design/icons';
import posthog from 'posthog-js';
import { ROLE_TYPES } from '../../utils/DataConstants';
import { WORK_ORDER_STATUSES } from '../../constants/WorkOrderStatuses';
import AudioPlayer from '../MessageBubble/AudioPlayer';
import { getTimeDuration } from '../../utils/DataFormatterUtils';
import { wasServiceCallSectionEdited } from '../../utils/ServiceCallRevisionUtils';
import ReviseServiceCallModal from '../revise_service_call_modal/ReviseServiceCallModal';
import ServiceCallRevisionsDropdown from '../service_call_revisions_dropdown/ServiceCallRevisionsDropdown';

const SupplierServiceCallDetails: FC<any> = ({
	currentUser,
	workOrder,
	handleManualCheckOut,
	showApproveModal,
	showRejectModal,
	handleReschedule,
	handleManualCheckIn,
	isReadOnly,
	isClientView,
	copyServiceCall,
	handleTechEnRoute,
	actionButtonLoading,
	refreshWorkOrder,
}): React.ReactElement => {
	const userType = useMemo(() => currentUser.userType, [currentUser.userType]);

	const isPrivateSupplier = useMemo(() => isInternalTech(currentUser), [currentUser]);

	const isTechEnRouteEnabled = posthog.isFeatureEnabled('tech-enroute-status');

	const differentAssignedSupplier = useMemo(
		() => isDifferentAssignedSupplierBasedOnView(workOrder, currentUser, isClientView),
		[currentUser, isClientView, workOrder]
	);

	const [editingState, setEditingState] = useState<{
		serviceCall: any;
		focus: 'checkIn' | 'checkOut';
	} | null>(null);
	const currentUserFacilityId = nullSafeGet('facility.id', currentUser);
	const editButton = useCallback(
		(serviceCall, focus: 'checkIn' | 'checkOut') => {
			if (!serviceCall || !serviceCall.checkInTime || !serviceCall.checkOutTime) return null;
			if (currentUserFacilityId !== serviceCall.supplierFacilityId) return null;
			return (
				<AccessPermissionChecker name={PERMISSION_NAMES.REVISE_SERVICE_CALLS}>
					<Button
						type="link"
						size="small"
						style={{ paddingLeft: 8 }}
						onClick={() => setEditingState({ serviceCall, focus })}
					>
						Edit <EditOutlined />
					</Button>
				</AccessPermissionChecker>
			);
		},
		[currentUserFacilityId]
	);

	const getServiceCallDetails = useCallback(
		(serviceCall) => {
			const scheduledAt = serviceCall.serviceScheduledAt
				? [
						<div>
							<div style={{ fontSize: 16 }}>
								Scheduled to arrive by{' '}
								<DateTimeHover
									timestamp={nullSafeGet('serviceScheduledAt', serviceCall)}
									dateFormat={'h:mm a z on MMM D'}
									showDate
									parseZone={false}
									timeZone={getWorkOrderTimeZoneId(workOrder)}
								/>
								.
							</div>
							{!serviceCall.checkOutStatus &&
							!isReadOnly &&
							(workOrder.status === 'TechScheduled' ||
								workOrder.status === 'TechRescheduled' ||
								workOrder.status === 'TechEnRoute') ? (
								<div className="mt-4 flex flex-row gap-x-4">
									<Button
										type="primary"
										key="manualCheckIn"
										disabled={!!actionButtonLoading}
										onClick={handleManualCheckIn}
									>
										Remote Check In
									</Button>
									{workOrder.status === 'TechEnRoute' ||
									!isTechEnRouteEnabled ||
									differentAssignedSupplier ? null : (
										<Button
											key="techEnRoute"
											loading={actionButtonLoading === 'techEnRoute'}
											disabled={disabledByAnotherAction(actionButtonLoading, 'techEnRoute')}
											onClick={handleTechEnRoute}
										>
											En route
										</Button>
									)}
									<Button
										key="reschedule"
										disabled={!!actionButtonLoading}
										onClick={handleReschedule}
									>
										Reschedule
									</Button>
								</div>
							) : null}
						</div>,
				  ]
				: [];

			const startedWorkAt = serviceCall.checkInTime
				? [
						<div style={{ fontSize: 16 }}>
							<div>
								Started work at{' '}
								<DateTimeHover
									timestamp={nullSafeGet('checkInTime', serviceCall)}
									dateFormat={'h:mm a z on MMM D'}
									showDate
									timeZone={getWorkOrderTimeZoneId(workOrder)}
								/>
								.
								{serviceCall.checkInGeoLocation ? null : nullSafeGet(
										`checkInByContact`,
										serviceCall
								  ) ? (
									<span style={{ color: 'rgba(0,0,0,0.45)' }}>
										{' '}
										(Checked in remotely by{' '}
										<UserName
											mode={'inline'}
											contact={nullSafeGet(`checkInByContact`, serviceCall)}
										/>
										)
									</span>
								) : null}
								{wasServiceCallSectionEdited(serviceCall, 'checkIn') ? (
									<span style={{ color: 'rgba(0,0,0,0.45)', fontStyle: 'italic' }}> (edited)</span>
								) : null}
								{editButton(serviceCall, 'checkIn')}
							</div>
							{serviceCall.checkInAudio ? (
								<div className="serviceCallNotesBlurb">
									<div>
										<AudioPlayer type="default" src={serviceCall.checkInAudio} />
									</div>
									{nullSafeGet('checkInAudioTranscription', serviceCall) ? (
										<div>Transcription: {serviceCall.checkInAudioTranscription}</div>
									) : null}
								</div>
							) : null}
							{serviceCall.checkInNotes && serviceCall.checkInNotes.length > 0 ? (
								<div className="serviceCallNotesBlurb">{serviceCall.checkInNotes}</div>
							) : null}
							{serviceCall.checkInImages && serviceCall.checkInImages.length > 0 ? (
								<ProtectedImageThumbnails
									style={{ marginTop: 16 }}
									imageWidth={64}
									imageHeight={64}
									photoStrings={serviceCall.checkInImages}
									role={userType}
									showDownloadAll
									imageDownloadName={`wo#${workOrder.id}_check_in_`}
								/>
							) : null}
							{!isReadOnly &&
							!serviceCall.checkOutStatus &&
							[WORK_ORDER_STATUSES.techWorkingOnSite, WORK_ORDER_STATUSES.partsRequested].includes(
								workOrder.status
							) ? (
								<div>
									<Button
										style={{ marginTop: 16 }}
										type="primary"
										key="manualCheckOut"
										onClick={handleManualCheckOut}
									>
										Remote Check Out
									</Button>
								</div>
							) : null}
						</div>,
				  ]
				: [];

			const endedWorkAt = serviceCall.checkOutTime
				? [
						<div style={{ fontSize: 16 }}>
							<div>
								Ended work at{' '}
								<DateTimeHover
									timestamp={nullSafeGet('checkOutTime', serviceCall)}
									dateFormat={'h:mm a z on MMM D'}
									showDate
									timeZone={getWorkOrderTimeZoneId(workOrder)}
								/>
								.
								{serviceCall.checkOutGeoLocation ? null : nullSafeGet(
										`checkOutByContact`,
										serviceCall
								  ) ? (
									<span style={{ color: 'rgba(0,0,0,0.45)' }}>
										{' '}
										(Checked out remotely by{' '}
										<UserName
											mode={'inline'}
											contact={nullSafeGet(`checkOutByContact`, serviceCall)}
										/>
										)
									</span>
								) : null}
								{wasServiceCallSectionEdited(serviceCall, 'checkOut') ? (
									<span style={{ color: 'rgba(0,0,0,0.45)', fontStyle: 'italic' }}> (edited)</span>
								) : null}
								{editButton(serviceCall, 'checkOut')}
							</div>
							{serviceCall.checkOutAudio ? (
								<div className="serviceCallNotesBlurb">
									<div>
										<AudioPlayer type="default" src={serviceCall.checkOutAudio} />
									</div>

									{nullSafeGet('checkOutAudioTranscription', serviceCall) ? (
										<div>Transcription: {serviceCall.checkOutAudioTranscription}</div>
									) : null}
								</div>
							) : null}
							{serviceCall.checkOutNotes && serviceCall.checkOutNotes.length > 0 ? (
								<div className="serviceCallNotesBlurb">{serviceCall.checkOutNotes}</div>
							) : null}
							{serviceCall.checkOutImages && serviceCall.checkOutImages.length > 0 ? (
								<ProtectedImageThumbnails
									style={{ marginTop: 16 }}
									imageWidth={64}
									imageHeight={64}
									photoStrings={serviceCall.checkOutImages}
									role={userType}
									showDownloadAll
									imageDownloadName={`wo#${workOrder.id}_check_out_`}
								/>
							) : null}
						</div>,
				  ]
				: [];

			const wasOnSiteFor = () => {
				if (!serviceCall || !serviceCall.checkOutTime || !serviceCall.checkInTime) {
					return [];
				}
				//Work completed by third party and is internal technician or a paying supplier admin user
				if (
					(isPrivateSupplier ||
						(isSupplierPayingCustomerAndAdmin(currentUser) &&
							isSubContractedWorkOrder(workOrder))) &&
					differentAssignedSupplier
				) {
					return [
						<div style={{ fontSize: 16 }}>
							{serviceCall.checkInTime && serviceCall.checkOutTime ? (
								<div>
									On site for{' '}
									{getTimeDuration(serviceCall.checkOutTime, serviceCall.checkInTime, {
										hideSeconds: true,
									}) || 'less than a minute'}
								</div>
							) : null}
							{!isReadOnly &&
							serviceCall.checkOutStatus === 'WaitingForReview' &&
							workOrder.status === 'WaitingForReview'
								? [
										<AccessPermissionChecker
											name={PERMISSION_NAMES.CAN_REVIEW_WORK_ORDER_COMPLETED}
										>
											<Button
												style={{ marginTop: 16 }}
												type="primary"
												key="approve"
												onClick={showApproveModal}
											>
												Approve Work
											</Button>
										</AccessPermissionChecker>,
										<AccessPermissionChecker
											name={PERMISSION_NAMES.CAN_REVIEW_WORK_ORDER_COMPLETED}
										>
											<Button
												key="reject"
												style={{ marginLeft: 16, marginTop: 16 }}
												onClick={showRejectModal}
											>
												Reject Work
											</Button>
										</AccessPermissionChecker>,
								  ]
								: null}
						</div>,
					];
				} else if (isPrivateSupplier && !differentAssignedSupplier) {
					//If work was completed by internal technicians
					return [
						<div style={{ fontSize: 16 }}>
							{serviceCall.checkInTime && serviceCall.checkOutTime ? (
								<div>
									On site for{' '}
									{getTimeDuration(serviceCall.checkOutTime, serviceCall.checkInTime, {
										hideSeconds: true,
									}) || 'less than a minute'}
								</div>
							) : null}
						</div>,
					];
				} else {
					return [
						<div style={{ fontSize: 16 }}>
							{serviceCall.checkInTime && serviceCall.checkOutTime ? (
								<div>
									On site for{' '}
									{getTimeDuration(serviceCall.checkOutTime, serviceCall.checkInTime, {
										hideSeconds: true,
									}) || 'less than a minute'}
								</div>
							) : null}
						</div>,
					];
				}
			};

			return [...scheduledAt, ...startedWorkAt, ...endedWorkAt, ...wasOnSiteFor()];
		},
		[
			differentAssignedSupplier,
			handleManualCheckIn,
			handleManualCheckOut,
			handleReschedule,
			isPrivateSupplier,
			isReadOnly,
			showApproveModal,
			showRejectModal,
			userType,
			workOrder,
			editButton,
		]
	);

	const ConditionalWrapper = ({ condition, wrapper, children }) =>
		condition ? wrapper(children) : children;

	return (
		<>
			<ConditionalWrapper
				condition={isReadOnly}
				wrapper={(children) => <Card title="Service Calls">{children}</Card>}
			>
				{getSupplierWorkOrderServiceCalls(workOrder, currentUser, isClientView).map((sc, scIdx) => [
					<div key={sc.id + '-1'}>
						<div style={{ marginBottom: 24 }}>
							<div
								className="serviceCallSupplierName flex flex-wrap items-center gap-x-3"
								style={{ marginBottom: 8 }}
							>
								<span>
									{nullSafeGet('supplierFacility.name', sc)}
									{isClientView ||
									nullSafeGet('originalWorkOrder.displayStatus', workOrder) === 'Completed' ||
									nullSafeGet('originalWorkOrder.displayStatus', workOrder) ===
										'Cancelled' ? null : sc.checkOutTime ? (
										<Popover content="Copy this service call to your client view">
											<Button
												onClick={() => copyServiceCall(sc)}
												icon={<CopyOutlined />}
												style={{ marginLeft: 8 }}
											/>
										</Popover>
									) : null}
								</span>
								<ServiceCallRevisionsDropdown serviceCall={sc} workOrder={workOrder} />
							</div>
							{sc.leadTechnicianEmail ? (
								<div
									className="materialCard__primaryTitle"
									style={{
										marginBottom: 0,
										display: 'flex',
										alignItems: 'center',
									}}
								>
									<ContactAvatar
										size="large"
										hidePopover={true}
										style={{ marginRight: 16 }}
										contact={sc.leadTechnicianEmailContact}
										userType={userType}
									/>
									<div>
										<div className="materialCard__title">
											{nullSafeGet('leadTechnicianEmailContact.nameGiven', sc)}{' '}
											{nullSafeGet('leadTechnicianEmailContact.nameFamily', sc)}
										</div>
										<div className="materialCard__subtitle">
											{nullSafeGet('leadTechnicianEmailContact.title', sc)}{' '}
											{nullSafeGet('leadTechnicianEmailContact.department', sc)}
										</div>
									</div>
								</div>
							) : null}
						</div>
						<Timeline>
							{getServiceCallDetails(sc).map((scd, idx) => (
								<Timeline.Item key={idx}>{scd}</Timeline.Item>
							))}
						</Timeline>
					</div>,
					scIdx === nullSafeGetOrElse('associatedServiceCalls', workOrder, []).length - 1 ? null : (
						<Divider key={sc.id + '-2'} />
					),
				])}
			</ConditionalWrapper>
			<ReviseServiceCallModal
				key={
					editingState
						? `${editingState.serviceCall.id}-${editingState.focus}`
						: 'revise-modal-closed'
				}
				visible={editingState !== null}
				workOrder={workOrder}
				serviceCall={editingState ? editingState.serviceCall : undefined}
				initialFocus={editingState ? editingState.focus : undefined}
				userType={ROLE_TYPES.SUPPLIER}
				onCancel={() => setEditingState(null)}
				onSuccess={refreshWorkOrder}
			/>
		</>
	);
};

const mapStateToProps = (state) => ({
	currentUser: state.session.currentUser,
	isClientView: state.clientView.isClientView,
});

export default connect(mapStateToProps)(SupplierServiceCallDetails);
